import { CustomerViewModel } from '@models/api-customers.models';
import { UserData } from '@utils/gtm-tracking/model/UserData';
import { Address } from '@models/api-orders.models';
import { splitName } from '@utils/split-name.helper';
import { ReserveInStoreUserInformation } from '@features/reserve-in-store/reserve-in-store';
import { removeEmpty } from '@utils/remove-empty.helper';
import { CheckoutData } from '@accelerator/checkout/model/checkout.model';

type CheckoutViewModel = { userEmail: string } & CheckoutData['billingAddress'];

/**
 * Maps the data from several types of data models to the tracking user data
 * @param data CustomerViewModel | Address | ReserveInStoreUserInformation
 * @param hasConsent boolean
 * @returns UserData
 */
export const mapToTrackingUserData = (
    data: CustomerViewModel | Address | ReserveInStoreUserInformation | CheckoutViewModel,
    hasConsent: boolean = false
): UserData => {
    const defaultUserData: UserData = {
        userId: hasConsent ? 'not set' : 'not consented',
        customerType: 'private', // for now, we only have B2C customers
        loginStatus: 'not logged in'
    };

    if (!hasConsent) {
        return defaultUserData;
    }

    if ('customerNo' in data && (data.customerNo || data.email)) {
        const customerData: Partial<UserData> = removeEmpty(mapFromCustomerViewModel(data, defaultUserData));
        return { ...defaultUserData, ...customerData };
    }

    if ('zipCode' in data && 'email' in data) {
        const customerData: Partial<UserData> = removeEmpty(mapFromAddress(data, defaultUserData));
        return { ...defaultUserData, ...customerData };
    }

    if ('customerEmail' in data) {
        const customerData: Partial<UserData> = removeEmpty(mapFromReserveInStoreViewModel(data, defaultUserData));
        return { ...defaultUserData, ...customerData };
    }

    // is because is an anonyous user and is filling the checkout form billing address
    if ('userEmail' in data) {
        const customerData: Partial<UserData> = removeEmpty(mapFromCheckoutForm(data, defaultUserData));
        return { ...defaultUserData, ...customerData };
    }

    return defaultUserData;
};

const mapFromCustomerViewModel = (data: CustomerViewModel, defaultUserData: UserData): Partial<UserData> => ({
    userId: data.email ?? data.customerNo ?? defaultUserData.userId,
    loginStatus: 'logged in',
    city: data.address?.city,
    country: data.address?.country,
    firstname: data.name,
    lastname: data.name2,
    phoneNumber: data.phoneNumber1 ?? data.phoneNumber2,
    postCode: data.address?.postalCode,
    street: data.address
        ? `${data.address?.street}${data.address?.street2 ? ', ' + data.address?.street2 : ''}`
        : undefined
});

const mapFromAddress = (data: Address, defaultUserData: UserData): Partial<UserData> => ({
    userId: data.email ?? defaultUserData.userId,
    loginStatus: 'not logged in',
    city: data?.city,
    country: data.country,
    phoneNumber: data.phoneNumber1 ?? data.phoneNumber2,
    postCode: data.zipCode,
    street: `${data?.street}`,
    ...splitName(data.name)
});

const mapFromReserveInStoreViewModel = (
    data: ReserveInStoreUserInformation,
    defaultUserData: UserData
): Partial<UserData> => ({
    userId: data.customerEmail || defaultUserData.userId,
    loginStatus: 'not logged in',
    phoneNumber: data.customerMobile,
    ...splitName(data.customerName)
});

const mapFromCheckoutForm = (data: CheckoutViewModel, defaultUserData: UserData): Partial<UserData> => ({
    userId: data.userEmail || defaultUserData.userId,
    loginStatus: 'not logged in',
    city: data?.city,
    phoneNumber: data.phoneNumber,
    postCode: data.zipCode,
    street: `${data?.streetAddress}`,
    ...splitName(data.fullName)
});
